'use client';

import Link from 'next/link';
import Image from 'next/image';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useIsMobile } from '@/store/viewport-context';
import type { CarruselItems } from '@/types';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

export type BannerDestacadosHandle = {
  goTo: (index: number) => void;
};

type Props = {
  carItems: CarruselItems[];
  onSlideChange?: (index: number) => void; // ← nuevo
};

const BannerDestacados = forwardRef<BannerDestacadosHandle, Props>(
  ({ carItems, onSlideChange }, ref) => {
    const isMobile = useIsMobile();
    const sliderRef = useRef<Slider | null>(null);

    // Orden real por "orden"
    const ordered = useMemo(
      () => (carItems ?? []).slice().sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
      [carItems]
    );

    // Exponer goTo() al padre (header)
    useImperativeHandle(ref, () => ({
      goTo: (i: number) => sliderRef.current?.slickGoTo(i),
    }));

    const settings = {
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 4000,
      arrows: false,
      dots: false, // usamos los chips del header
      beforeChange: (_: number, next: number) => onSlideChange?.(next),
    } as const;

    if (ordered.length === 0) return null;

    return (
      <div className="w-full mx-auto sm:rounded-xl overflow-hidden shadow-[0_0_1px_1px] shadow-violet-900/40 max-[400px]:h-[200px] max-md:h-[350px] md:h-[380px]">
        <Slider ref={sliderRef} {...settings}>
          {ordered.map((item, index) => {
            const evento = item.evento!;
            const imagen = isMobile
              ? evento?.bannerMobile || '/bebop/placeholder/banner_ticketera_mobile.png'
              : evento?.bannerDesktop || '/bebop/placeholder/banner_ticketera_desktop.png';

            return (
              <div key={item.id} className="max-[400px]:h-[200px] max-md:h-[350px] md:h-[380px]">
                <Link href={`/evento/${evento?.id}`} className="relative block h-full overflow-hidden">
                  <Image
                    src={imagen}
                    alt={evento?.nombre}
                    fill
                    className="object-fill h-full"
                    priority={index === 0}
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                </Link>
              </div>
            );
          })}
        </Slider>
      </div>
    );
  }
);

BannerDestacados.displayName = 'BannerDestacados';

export default BannerDestacados;
